import React, { useEffect, useRef } from "react";
import {
    RichTextEditor,
    MenuControlsContainer,
    MenuSelectHeading,
    MenuDivider,
    MenuButtonBold,
    MenuButtonItalic,
    MenuButtonStrikethrough,
    MenuButtonOrderedList,
    MenuButtonBulletedList,
    MenuSelectFontFamily,
    MenuSelectFontSize,
    MenuButtonTextColor,
    MenuButtonHighlightColor,
    MenuButtonUnderline,
    MenuButtonCode,
    MenuButtonUndo,
    MenuButtonRedo,
    type RichTextEditorRef,
} from "mui-tiptap";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-font-family";
import { Highlight } from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import { MesTextEditorContent } from "./MesTextEditorContent";
import { Extension_FontSize } from "./Extension_FontSize";

interface MesTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    visible?: boolean;
    defaultFontSize?: number;
}

const MesTextEditor: React.FC<MesTextEditorProps> = ({ value, onChange, visible = true }) => {
    const rteRef = useRef<RichTextEditorRef>(null);
    const fontFamilySelectRef = useRef<HTMLDivElement>(null);

    // 단축키 설정 및 에디터 내용 동기화 로직 훅 호출
    MesTextEditorContent({
        value,
        visible,
        rteRef,
        fontFamilySelectRef
    });

    if (!visible) return null;

    return (
        <div className="mes-editor-container h-full w-full rounded-md bg-white flex flex-col overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-shadow">
            <style>{`
                /* Placeholder 스타일 */
                .mes-editor-container .ProseMirror p.is-editor-empty:first-child::before {
                    color: #adb5bd;
                    content: attr(data-placeholder);
                    float: left;
                    height: 0;
                    pointer-events: none;
                    white-space: pre-wrap; /* 개행(\n)을 인식하도록 설정 */
                }
            `}</style>
            <RichTextEditor
                ref={rteRef}
                extensions={[
                    StarterKit.configure({
                        // 기본 리스트 단축키 비활성화를 위해 StarterKit 내부의 리스트 확장 제외
                        bulletList: false,
                        orderedList: false,
                    }),
                    // 커스텀 단축키를 적용한 BulletList
                    BulletList.extend({
                        addKeyboardShortcuts() {
                            return {
                                'Mod-.': () => this.editor.commands.toggleBulletList(),
                            };
                        },
                    }),
                    // 커스텀 단축키를 적용한 OrderedList
                    OrderedList.extend({
                        addKeyboardShortcuts() {
                            return {
                                'Mod-/': () => this.editor.commands.toggleOrderedList(),
                            };
                        },
                    }),
                    Underline,
                    TextStyle,
                    Color,
                    FontFamily,
                    Highlight.configure({ multicolor: true }),
                    Extension_FontSize,
                    Placeholder.configure({
                        placeholder: 'ctrl + shift + . : 글꼴 변경\nctrl + . : 기호 목록\nctrl + / : 번호 목록',
                    }),
                ]}
                content={value}
                onUpdate={({ editor }) => {
                    onChange(editor.getHTML());
                }}
                // 에디터가 부모 컨테이너의 전체 높이를 차지하도록 flex 및 height 설정
                className="flex-1 flex flex-col min-h-0"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    // 1. mui-tiptap 내부의 툴바 다음 요소(컨텐츠 래퍼)를 정확히 타겟팅
                    '& > div:nth-of-type(2)': {
                        flex: 1,
                        minHeight: 0, // flex 자식이 내용물 크기만큼 무한 팽창하는 것을 방지 (핵심)
                        overflowY: 'auto', // 래퍼 영역에서 스크롤 발생
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    // 2. 실제 텍스트가 입력되는 ProseMirror 영역
                    '& .ProseMirror': {
                        flex: 1,
                        outline: 'none',
                    }
                }}
                // 상단에 고정될 예쁜 Material UI 기반 툴바
                renderControls={() => (
                    <MenuControlsContainer>
                        <MenuButtonUndo />
                        <MenuButtonRedo />
                        <MenuDivider />
                        <div ref={fontFamilySelectRef}>
                            <MenuSelectFontFamily
                                options={[
                                    { label: "기본 글꼴", value: "" },
                                    { label: "여기어때 고딕", value: "'YeogiOttaeJalnanGothic', sans-serif" },
                                    { label: "맑은 고딕", value: "'Malgun Gothic', '맑은 고딕', sans-serif" },
                                    { label: "돋움", value: "'Dotum', '돋움', sans-serif" },
                                    { label: "굴림", value: "'Gulim', '굴림', sans-serif" },
                                    { label: "바탕", value: "'Batang', '바탕', serif" },
                                    { label: "궁서", value: "'Gungsuh', '궁서', serif" },
                                    { label: "나눔고딕", value: "'Nanum Gothic', sans-serif" },
                                    { label: "나눔명조", value: "'Nanum Myeongjo', serif" },
                                    { label: "견고딕", value: "'H2GTRE', 'HY견고딕', sans-serif" },
                                    { label: "Arial", value: "Arial" },
                                    { label: "Courier New", value: "Courier New, Courier" },
                                    { label: "Georgia", value: "Georgia" },
                                    { label: "Tahoma", value: "Tahoma" },
                                    { label: "Times New Roman", value: "Times New Roman" },
                                    { label: "Verdana", value: "Verdana" },
                                ]}
                            />
                        </div>
                        <MenuSelectFontSize />
                        <MenuDivider />
                        <MenuSelectHeading />
                        <MenuDivider />
                        <MenuButtonBold />
                        <MenuButtonItalic />
                        <MenuButtonUnderline />
                        <MenuButtonStrikethrough />
                        {/* <MenuButtonCode /> */}
                        <MenuDivider />
                        <MenuButtonTextColor />
                        <MenuButtonHighlightColor />
                        <MenuDivider />
                        <MenuButtonBulletedList />
                        <MenuButtonOrderedList />
                    </MenuControlsContainer>
                )}
                // 에디터 내부 컨텐츠 영역 스타일링
                editorProps={{
                    attributes: {
                        class: 'prose max-w-none outline-none p-4',
                        // 인라인 스타일로 강제했던 높이 제거 (sx에서 제어)
                    },
                }}
            />
        </div>
    );
};

export default MesTextEditor;